import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, FileText, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Empty, TabHeading, fmtDate } from "./shared";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtSize = (b) => (!b ? "" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

export default function DocumentsTab({ athleteId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents?athlete_id=${athleteId}`);
      setItems(data);
    } catch { toast.error("Failed to load documents."); } finally { setLoading(false); }
  }, [athleteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const del = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Document deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  if (loading) return <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="af-documents">
      <TabHeading>Document Center</TabHeading>
      {items.length === 0 ? (
        <Empty testid="af-documents-empty">No documents yet. Upload documents or generate a report from Quick Actions.</Empty>
      ) : (
        <div className="space-y-2" data-testid="af-documents-list">
          {items.map((d) => (
            <div key={d.id} data-testid={`af-document-${d.id}`} className="bg-[#121212] border border-white/10 p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={18} className="text-white/50 shrink-0" />
                <div className="min-w-0">
                  <p className="font-bold truncate">{d.name}</p>
                  <p className="text-white/40 text-xs">{[d.category, fmtSize(d.size), fmtDate(d.created_at)].filter(Boolean).join(" · ")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <a data-testid={`af-document-open-${d.id}`} href={`${BACKEND}/api/documents/${d.id}/file`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><ExternalLink size={13} /> Open</a>
                <button data-testid={`af-document-del-${d.id}`} onClick={() => del(d.id)} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
